import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PagoCuotasPrestamosModel,
  ConstitucionPrestamosModel,
  PrestamosModel,
  DeudaACortoPlazoPrestamosModel
} from './prestamos.model';
import { ResultModel } from '../../../shared/result.model';
import { ApiResponse } from '../../../models/base';
import { unwrapV2, V2ApiResponse } from '../../../modules_v2/shared/utils/v2-api-response.util';

const PRESTAMOS_ENDPOINT = '/prestamos';
const CONSTITUCION_ENDPOINT = '/constitucionprestamos';
const DEUDACORTOPLAZO_ENDPOINT = '/deudasacortoplazoprestamos';
const PAGOCUOTAS_ENDPOINT = '/pagocuotasprestamos';
const V2_ENDPOINT = '/api/v2/loans';

@Injectable({
  providedIn: 'root',
})
export class PrestamosService {
  constructor(private http: HttpClient) {}

  public downloadFileAttachment(uid: string): Promise<Blob> {
    let params = new HttpParams();
    params = params.append('prestamosId', uid);

    return firstValueFrom(
      this.http.get(environment.apiUrl + '/prestamos/file/downloadexcel', {
        responseType: 'blob',
        params: params,
      })
    ).then((response) => {
      return new Blob([response], { type: 'application/vnd.ms-excel' });
    });
  }

  public get(uid: string): Promise<PrestamosModel> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<PrestamosModel>>(environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(uid))),
      'No se ha encontrado el préstamo.'
    );
  }

  public list(offset?: number, limit?: number): Promise<ResultModel<PrestamosModel>> {
    return unwrapV2(
      firstValueFrom(this.http.get<V2ApiResponse<PrestamosModel[]>>(environment.apiUrl + V2_ENDPOINT)),
      'No se pudieron obtener los préstamos.'
    ).then((list) => {
      const result = new ResultModel<PrestamosModel>();
      result.list = Array.isArray(list) ? list : [];
      result.totalItems = result.list.length;
      return result;
    });
  }

  public add(prestamos: PrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + PRESTAMOS_ENDPOINT, prestamos)
    ).then((response) => response.data);
  }

  public edit(prestamos: PrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + PRESTAMOS_ENDPOINT, prestamos)
    ).then((response) => response.data);
  }

  public editFileAttachment(prestamos: PrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + PRESTAMOS_ENDPOINT + '/file', prestamos)
    ).then((response) => response.data);
  }

  public delete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('prestamosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + PRESTAMOS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public constitucionList(prestamosId: string): Promise<ConstitucionPrestamosModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<ConstitucionPrestamosModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(prestamosId) + '/constitutions'
        )
      ),
      'No se pudieron obtener las constituciones.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public constitucionCreate(constitucion: ConstitucionPrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, constitucion)
    ).then((response) => response.data);
  }

  public constitucionUpdate(constitucion: ConstitucionPrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, constitucion)
    ).then((response) => response.data);
  }

  public constitucionDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('constitucionprestamosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + CONSTITUCION_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public deudaACortoPlazoList(
    deudaacortoplazoUid: string
  ): Promise<DeudaACortoPlazoPrestamosModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<DeudaACortoPlazoPrestamosModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(deudaacortoplazoUid) + '/short-term-debts'
        )
      ),
      'No se pudieron obtener las deudas a corto plazo.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public deudaACortoPlazoCreate(
    deudaACortoPlazo: DeudaACortoPlazoPrestamosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoUpdate(
    deudaACortoPlazo: DeudaACortoPlazoPrestamosModel
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, deudaACortoPlazo)
    ).then((response) => response.data);
  }

  public deudaACortoPlazoDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('deudasacortoplazoprestamosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + DEUDACORTOPLAZO_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }

  public pagoCoutasList(pagocuotasUid: string): Promise<PagoCuotasPrestamosModel[]> {
    return unwrapV2(
      firstValueFrom(
        this.http.get<V2ApiResponse<PagoCuotasPrestamosModel[]>>(
          environment.apiUrl + V2_ENDPOINT + '/' + encodeURIComponent(pagocuotasUid) + '/installment-payments'
        )
      ),
      'No se pudieron obtener los pagos de cuotas.'
    ).then((list) => (Array.isArray(list) ? list : []));
  }

  public pagoCoutasCreate(pagoDeCuotas: PagoCuotasPrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.post<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasUpdate(pagoDeCuotas: PagoCuotasPrestamosModel): Promise<boolean> {
    return firstValueFrom(
      this.http.put<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, pagoDeCuotas)
    ).then((response) => response.data);
  }

  public pagoCoutasDelete(uid: string): Promise<boolean> {
    let params = new HttpParams();
    params = params.append('pagocuotasprestamosId', uid);

    return firstValueFrom(
      this.http.delete<ApiResponse<boolean>>(environment.apiUrl + PAGOCUOTAS_ENDPOINT, {
        params: params,
      })
    ).then((response) => response.data);
  }
}